import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams, useLocation, useNavigate, Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SEO } from "@/data/seo";
import { CATEGORIES, SITE } from "@/data/config";
import { SERVICES } from "@/data/services";
import { INDUSTRIES } from "@/data/industries";
import { NEEDS } from "@/data/needs";
import ServiceCard from "@/components/ServiceCard";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";
import CTABlock from "@/components/CTABlock";
import PricingFAQ from "@/components/PricingFAQ";
import GrowthLinks from "@/components/GrowthLinks";
import { Input } from "@/components/ui/input";

// Six flagship services to feature above the fold when no filter is active.
// Mixed by engagement type: audits (fixed), fixed-scope services, projects.
const FEATURED_SLUGS = [
  "strategy-call",
  "brand-and-website-review",
  "brand-and-website-launch-foundation",
  "website-growth-build",
  "google-business-profile-optimization",
  "marketing-support-growth",
];

// Five ways to work with Kostaja. Card content that mirrors the strategic model.
const ENGAGEMENT_PATHS = [
  {
    key: "audit",
    label: "Audit",
    tagline: "Best when something feels wrong but the next move is unclear.",
    receive: "A practical review, priority recommendations, and a clearer starting path.",
    cta: { label: "See audits", href: "/services", external: false },
  },
  {
    key: "fixed",
    label: "Fixed-Scope Service",
    tagline: "Best when the deliverable is specific and the need is obvious.",
    receive: "A defined piece of work with clear inputs, outputs, and timeline.",
    cta: { label: "Browse services", href: "/services", external: false },
  },
  {
    key: "project",
    label: "Project",
    tagline: "Best when the issue crosses brand, website, content, and systems.",
    receive: "A scoped proposal built around the business outcome and the work required.",
    cta: { label: "Request scope", href: "/contact", intent: "quote", external: false },
  },
  {
    key: "ongoing",
    label: "Ongoing Support",
    tagline: "Best when consistency is the problem.",
    receive: "Recurring marketing, content, website, or visibility support on agreed priorities.",
    cta: { label: "Discuss support", href: "/contact", intent: "quote", external: false },
  },
];

const Services = () => {
  useSeo(SEO.services);
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Seed initial filter state from legacy ?category=/?billing=/?industry= URLs
  // so old bookmarks still land in the right view. After mount we strip the
  // query string so Google is never fed crawlable filter URLs from the SPA.
  const initialCat = params.get("category") || "all";
  const initialBilling = params.get("billing") || "all";
  const initialIndustry = params.get("industry") || "all";
  const [category, setCategory] = useState(initialCat);
  const [billing, setBilling] = useState(initialBilling);
  const [industry, setIndustry] = useState(initialIndustry);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (location.search) {
      navigate("/services", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFiltered = category !== "all" || billing !== "all" || industry !== "all" || q.trim().length > 0;

  const filtered = useMemo(() => {
    return SERVICES.filter((s) => {
      if (category !== "all" && s.category !== category) return false;
      if (billing !== "all" && s.billing !== billing) return false;
      if (industry !== "all") {
        const ind = INDUSTRIES.find((i) => i.slug === industry);
        const list = ind ? (ind.recommendedServices || ind.bestServices || []) : [];
        if (!list.includes(s.slug)) return false;
      }
      if (q) {
        const needle = q.toLowerCase();
        const hay = [s.name, s.outcome, s.bestFor, (s.tags || []).join(" ")].join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [category, billing, industry, q]);

  const featured = useMemo(
    () => FEATURED_SLUGS.map((slug) => SERVICES.find((s) => s.slug === slug)).filter(Boolean),
    []
  );

  // Category cards: count + up to 3 preview services for the collection.
  const collections = useMemo(
    () =>
      CATEGORIES.map((c) => {
        const items = SERVICES.filter((s) => s.category === c.slug);
        return { ...c, count: items.length, preview: items.slice(0, 3) };
      }),
    []
  );

  const clearFilters = () => {
    setCategory("all");
    setBilling("all");
    setIndustry("all");
    setQ("");
    setShowAll(false);
  };

  return (
    <main data-testid="services-page" className="bg-black">
      {/* HERO */}
      <section className="bg-black text-white border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <Crumbs items={[{ to: "/", label: "Home" }, { label: "Services" }]} />
          <h1 className="mt-6 text-5xl sm:text-7xl lg:text-[104px] font-black tracking-[-0.03em] leading-[0.9]">
            <span className="block">Four ways</span>
            <span className="block">to work with <span className="k-yellow">Kostaja.</span></span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg lg:text-xl text-white/75 leading-relaxed">
            Tell us the outcome. Kostaja names the sequence. You do not need to pick a package or diagnose your own problem before the first conversation.
          </p>
        </div>
      </section>

      {/* FOUR HUBS — the primary services landing */}
      <section
        data-testid="services-four-hubs"
        className="bg-black border-b border-white/10"
      >
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/15">
            {[
              {
                n: "01",
                to: "/services/brand",
                title: "Brand",
                desc: "Build a business people recognize, understand, and trust. Strategy, identity, messaging, applications.",
              },
              {
                n: "02",
                to: "/services/websites",
                title: "Websites",
                desc: "A website that looks the part, explains the business, and earns the next step. Strategy, design, build.",
              },
              {
                n: "03",
                to: "/services/marketing",
                title: "Marketing",
                desc: "Put the business in front of the right people and give them a reason to act. Local search, social, content, campaigns.",
              },
              {
                n: "04",
                to: "/services/ongoing-partner",
                title: "Ongoing Partner",
                desc: "Senior creative and marketing support without building the whole department in-house.",
              },
            ].map((it) => (
              <li key={it.n} className="bg-black">
                <Link
                  to={it.to}
                  data-testid={`services-hub-${it.n}`}
                  className="group block p-10 lg:p-14 h-full hover:bg-[#FFC600] hover:text-black transition-colors"
                >
                  <div className="flex items-baseline justify-between gap-4 mb-10">
                    <span className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70">{it.n}</span>
                    <span className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70">Open the path</span>
                  </div>
                  <h2 className="text-5xl lg:text-7xl font-black tracking-[-0.02em] leading-[0.92]">
                    {it.title}
                  </h2>
                  <p className="mt-8 text-base lg:text-lg leading-relaxed opacity-85 max-w-md">
                    {it.desc}
                  </p>
                  <span className="mt-12 inline-flex items-center gap-2 text-sm font-semibold">
                    See how <span aria-hidden>→</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-sm text-white/60 max-w-xl">
              Not sure where to start? <Link to="/contact" data-testid="services-diagnose" className="text-[#FFC600] font-semibold hover:text-white">Show us what&apos;s not working.</Link> Kostaja will name the right first move.
            </p>
            <div className="flex gap-3">
              <PrimaryButton to="/contact" testId="services-hero-start">Start a Project</PrimaryButton>
              <SecondaryButton dark to="/portfolio" testId="services-hero-view-work">View Work</SecondaryButton>
            </div>
          </div>
        </div>
      </section>

      {/* WAYS TO WORK WITH KOSTAJA — hidden, redundant with the four hubs above */}
      <section
        data-testid="ways-to-work"
        className="hidden"
      >
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 mb-10">
            <div className="lg:col-span-6">
              <Overline className="mb-3">Ways to work with Kostaja</Overline>
              <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">
                Five engagement paths. One goal: choose the right one.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-10">
              <p className="text-white/70 max-w-md text-base leading-relaxed">
                Kostaja does not push every visitor toward the largest project. The right starting point depends on what is unclear, what is already working, and what needs to happen next. Larger work is scoped after review because the right price depends on the actual problem.
              </p>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/15">
            {ENGAGEMENT_PATHS.map((p, idx) => (
              <li key={p.key} className="bg-black p-6 lg:p-7 flex flex-col gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55">
                    0{idx + 1}
                  </div>
                  <h3 className="mt-2 text-xl lg:text-2xl font-black tracking-tight leading-tight text-white">
                    {p.label}
                  </h3>
                </div>
                <p className="text-sm text-white/75 leading-snug">{p.tagline}</p>
                <div className="border-t border-white/15 pt-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#FFC600] mb-2">
                    You receive
                  </div>
                  <p className="text-sm text-white/85 leading-snug">{p.receive}</p>
                </div>
                <div className="mt-auto pt-2">
                  {p.cta.external ? (
                    <a
                      href={p.cta.href}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`ways-to-work-${p.key}-cta`}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#FFC600] hover:text-white transition-colors"
                    >
                      {p.cta.label} <span aria-hidden>→</span>
                    </a>
                  ) : (
                    <Link
                      to={p.cta.href}
                      state={p.cta.intent ? { intent: p.cta.intent } : undefined}
                      data-testid={`ways-to-work-${p.key}-cta`}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#FFC600] hover:text-white transition-colors"
                    >
                      {p.cta.label} <span aria-hidden>→</span>
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-8 border border-white/15 p-6 lg:p-8">
            <Overline color="yellow" className="mb-3">How pricing works</Overline>
            <p className="text-white/85 text-base leading-relaxed max-w-3xl">
              Some work is fixed-scope and priced publicly. Some work should be scoped after review because the right price depends on what needs to change, how much already exists, and how directly the work supports the next business outcome. Kostaja uses the simplest appropriate starting point, whether that is an audit, a defined service, a custom project, ongoing support, or a self-directed resource.
            </p>
          </div>
        </div>
      </section>

      {/* SHOP BY OUTCOME */}
      <section className="hidden" data-testid="services-shop-by-outcome">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 mb-8">
            <div className="lg:col-span-6">
              <Overline className="mb-3">Shop by outcome</Overline>
              <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">
                Start from the problem, not the deliverable.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-10">
              <p className="text-white/70 max-w-md text-base">
                Most owners pick a service before naming the bottleneck. Pick the loudest problem below. Each opens a curated sequence, not a menu.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/15">
            {NEEDS.map((n, idx) => (
              <Link
                key={n.slug}
                to={`/needs/${n.slug}`}
                data-testid={`svc-outcome-${n.slug}`}
                className="group bg-black p-6 lg:p-7 hover:bg-[#FFC600] hover:text-black transition-colors flex flex-col gap-3"
              >
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">0{idx + 1} · Outcome</span>
                <h3 className="text-xl lg:text-2xl font-black tracking-tight leading-tight">{n.name}</h3>
                <p className="text-sm opacity-80 leading-snug line-clamp-3">{n.summary}</p>
                <span className="mt-auto text-sm font-semibold inline-flex items-center gap-2">Open the path <span aria-hidden>→</span></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BROWSE BY COLLECTION */}
      <section className="hidden" data-testid="services-collections">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 mb-8">
            <div className="lg:col-span-6">
              <Overline className="mb-3">Browse by collection</Overline>
              <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">
                Twelve collections. Same rules.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-10">
              <p className="text-white/70 max-w-md text-base">
                Every collection is scoped in plain English, priced where possible, and paired with a clear next step. Open a collection to see everything in it.
              </p>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/15">
            {collections.map((c) => (
              <li key={c.slug} className="bg-black">
                <button
                  type="button"
                  data-testid={`svc-collection-${c.slug}`}
                  onClick={() => {
                    setCategory(c.slug);
                    setShowAll(false);
                    setTimeout(() => {
                      document.getElementById("services-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 30);
                  }}
                  className="group w-full text-left p-6 lg:p-7 flex flex-col gap-3 hover:bg-[#FFC600] hover:text-black transition-colors h-full"
                >
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">Collection</span>
                    <span className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">{c.count} {c.count === 1 ? "service" : "services"}</span>
                  </div>
                  <h3 className="text-xl lg:text-2xl font-black tracking-tight leading-tight">{c.name}</h3>
                  <ul className="text-sm opacity-80 leading-snug space-y-1">
                    {c.preview.map((p) => (
                      <li key={p.slug} className="line-clamp-1">{p.name}</li>
                    ))}
                    {c.count > 3 && (
                      <li className="text-[11px] uppercase tracking-[0.18em] font-semibold opacity-70">+ {c.count - 3} more</li>
                    )}
                  </ul>
                  <span className="mt-auto text-sm font-semibold inline-flex items-center gap-2">Open collection <span aria-hidden>→</span></span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FILTER BAR (sticky) */}
      {/* FULL CATALOG PREAMBLE — reframes the marketplace as an SEO-accessible drill-down */}
      <section
        data-testid="services-catalog-lead"
        className="bg-black border-b border-white/10"
      >
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20">
          <Overline className="mb-4">Full service catalog</Overline>
          <h2 className="text-3xl lg:text-5xl font-black tracking-[-0.02em] leading-[0.95] max-w-3xl">
            Prefer to see everything Kostaja does?
          </h2>
          <p className="mt-6 max-w-2xl text-white/70 text-base lg:text-lg leading-relaxed">
            Every deliverable is available as a standalone engagement below, searchable and filterable. Most Kostaja engagements combine several. You do not have to pick a specific one to start a conversation.
          </p>
        </div>
      </section>

      <section id="services-results" className="bg-black border-b border-white/10 sticky top-16 lg:top-20 z-30" data-testid="services-filters">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
          <div className="lg:col-span-4 flex items-center gap-3">
            <Input
              data-testid="services-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search services, outcomes, or tags"
              className="rounded-none border border-white/15 h-12 text-base"
            />
          </div>
          <div className="lg:col-span-3">
            <select
              data-testid="filter-category"
              className="w-full h-12 border border-white/20 px-3 bg-black text-white"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-label="Filter by category"
            >
              <option value="all">All categories</option>
              {CATEGORIES.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="lg:col-span-3">
            <select
              data-testid="filter-industry"
              className="w-full h-12 border border-white/20 px-3 bg-black text-white"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              aria-label="Filter by industry"
            >
              <option value="all">All industries</option>
              {INDUSTRIES.map((i) => (
                <option key={i.slug} value={i.slug}>{i.name}</option>
              ))}
            </select>
          </div>
          <div className="lg:col-span-2">
            <select
              data-testid="filter-billing"
              className="w-full h-12 border border-white/20 px-3 bg-black text-white"
              value={billing}
              onChange={(e) => setBilling(e.target.value)}
              aria-label="Filter by billing"
            >
              <option value="all">All billing</option>
              <option value="one-time">One-time</option>
              <option value="project">Project</option>
              <option value="monthly">Monthly</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </div>
      </section>

      {/* RESULTS: featured (default) OR filtered grid */}
      <section className="bg-black" data-testid="services-results-section">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-10 lg:py-16">
          {isFiltered ? (
            <>
              <div className="flex items-baseline justify-between mb-8">
                <Overline data-testid="results-count">
                  {filtered.length} result{filtered.length === 1 ? "" : "s"}
                </Overline>
                <button
                  data-testid="filters-clear"
                  className="text-sm font-semibold underline underline-offset-4 hover:text-[#FFC600]"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              </div>
              {filtered.length === 0 ? (
                <div data-testid="services-empty" className="border border-white/15 p-10 text-center">
                  <h3 className="text-2xl font-black tracking-tight">No services match those filters.</h3>
                  <p className="mt-2 text-white/70">Try widening the category or clearing the search.</p>
                  <div className="mt-6 flex justify-center gap-3">
                    <PrimaryButton to="/contact" testId="services-empty-contact">Tell us what you need</PrimaryButton>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                  {filtered.map((s) => (
                    <ServiceCard key={s.slug} service={s} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
                <div>
                  <Overline className="mb-2">Featured picks</Overline>
                  <h3 className="text-2xl lg:text-4xl font-black tracking-tight leading-[0.95]">
                    Six services owners start with most.
                  </h3>
                </div>
                {!showAll && (
                  <button
                    type="button"
                    data-testid="show-all-services"
                    onClick={() => setShowAll(true)}
                    className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-3 border-2 border-white/25 hover:border-[#FFC600] hover:text-[#FFC600] transition-colors"
                  >
                    View all {SERVICES.length} services <span aria-hidden>↓</span>
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {(showAll ? SERVICES : featured).map((s) => (
                  <ServiceCard key={s.slug} service={s} />
                ))}
              </div>
              {showAll && (
                <div className="mt-10 flex justify-center">
                  <button
                    type="button"
                    data-testid="collapse-services"
                    onClick={() => setShowAll(false)}
                    className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-3 border-2 border-white/25 hover:border-[#FFC600] hover:text-[#FFC600] transition-colors"
                  >
                    Collapse the wall <span aria-hidden>↑</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <PricingFAQ testId="services-pricing-faq" />

      <GrowthLinks testId="services-growth-links" />

      <CTABlock variant="default" />
    </main>
  );
};

export default Services;
